import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DrillSpec, DrillKeyframe, NormPoint } from '@4cm/core/drills/types';
import { DrillCanvasPreview, extractDrillSpec } from './DrillCanvasPreview';
import './DrillCanvasPlayer.css';

interface Props {
  /** Either a DrillSpec or a payload with a nested drill_spec — same shape
   *  contract as DrillCanvasPreview's `spec` / extractDrillSpec helpers. */
  spec: DrillSpec;
  /** Visual width budget in pixels (forwarded to the preview). */
  width?: number;
  /** Whether to autoplay on mount. Default false (coach presses play). */
  autoplay?: boolean;
}

const DEFAULT_KEYFRAME_DURATION = 2; // seconds — fallback when a keyframe omits it

/** Sums every keyframe's duration. Empty-keyframes spec returns 0. */
function totalDuration(keyframes: DrillKeyframe[]): number {
  return keyframes.reduce(
    (acc, kf) => acc + (Number.isFinite(kf.durationSec) ? kf.durationSec : DEFAULT_KEYFRAME_DURATION),
    0,
  );
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Compute interpolated entity positions at playback time `time` (seconds).
 *
 * Walks keyframes in order, applying each completed keyframe's positions
 * fully and interpolating linearly within the active keyframe. Per-entity
 * `objectTiming` (startPct / endPct) sub-windows the entity's motion within
 * the keyframe — useful for staggered runs where one player triggers, then
 * another follows.
 */
export function computeFrameAt(spec: DrillSpec, time: number): DrillSpec {
  const keyframes = spec.keyframes ?? [];
  if (keyframes.length === 0) return spec;

  const positions: Record<string, NormPoint> = {};
  for (const e of spec.entities) {
    positions[e.id] = e.position;
  }

  let cumulative = 0;
  for (let i = 0; i < keyframes.length; i++) {
    const kf = keyframes[i];
    const dur = Number.isFinite(kf.durationSec) ? kf.durationSec : DEFAULT_KEYFRAME_DURATION;
    const start = cumulative;
    const end = start + dur;

    if (time >= end) {
      for (const [id, target] of Object.entries(kf.positions)) {
        positions[id] = target;
      }
      cumulative = end;
      continue;
    }

    const localT = dur > 0 ? clamp((time - start) / dur, 0, 1) : 0;
    for (const [id, target] of Object.entries(kf.positions)) {
      const from = positions[id] ?? target;
      const timing = kf.objectTiming?.[id] ?? { startPct: 0, endPct: 1 };
      const window = Math.max(timing.endPct - timing.startPct, 0.0001);
      const t = clamp((localT - timing.startPct) / window, 0, 1);
      positions[id] = [lerp(from[0], target[0], t), lerp(from[1], target[1], t)];
    }
    break;
  }

  return {
    ...spec,
    entities: spec.entities.map((e) => ({ ...e, position: positions[e.id] ?? e.position })),
  };
}

/**
 * Read-only playback for a drill's keyframe sequence. A flipbook of the
 * authored layout — coach presses play, watches the movement, scrubs to any
 * point. No editing affordances; this is the "show me what this drill does"
 * surface.
 */
export function DrillCanvasPlayer({ spec, width, autoplay = false }: Props) {
  const keyframes = spec.keyframes ?? [];
  const total = useMemo(() => totalDuration(keyframes), [keyframes]);

  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(autoplay);
  const [speed, setSpeed] = useState(1);

  const lastTickRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  // Animation loop. Uses rAF deltas so playback matches wall-clock regardless
  // of refresh rate, and respects the `speed` multiplier.
  useEffect(() => {
    if (!playing || total === 0) {
      lastTickRef.current = null;
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    const step = (now: number) => {
      const last = lastTickRef.current ?? now;
      const dt = (now - last) / 1000;
      lastTickRef.current = now;
      setTime((prev) => {
        const next = prev + dt * speed;
        if (next >= total) {
          // Loop back to 0; the coach can pause if they want a single play.
          return 0;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [playing, speed, total]);

  const frame = useMemo(() => computeFrameAt(spec, time), [spec, time]);

  const handleScrub = useCallback((value: number) => {
    setTime(clamp(value, 0, total));
  }, [total]);

  const togglePlay = useCallback(() => setPlaying((p) => !p), []);
  const restart = useCallback(() => {
    setTime(0);
    setPlaying(true);
  }, []);

  // Keyframe boundaries — for the scrubber tickmarks and "jump to keyframe"
  // chips. Cumulative durations as fractions of total.
  const keyframeBoundaries = useMemo(() => {
    if (total === 0) return [];
    let acc = 0;
    return keyframes.map((kf) => {
      acc += Number.isFinite(kf.durationSec) ? kf.durationSec : DEFAULT_KEYFRAME_DURATION;
      return acc;
    });
  }, [keyframes, total]);

  const noKeyframes = keyframes.length === 0;

  return (
    <div className="drill-player" data-static={noKeyframes || undefined}>
      <DrillCanvasPreview spec={frame} width={width} />
      <div className="drill-player__controls" role="group" aria-label="Drill playback">
        <button
          type="button"
          className="drill-player__btn"
          onClick={togglePlay}
          disabled={noKeyframes}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? '⏸' : '▶'}
        </button>
        <button
          type="button"
          className="drill-player__btn"
          onClick={restart}
          disabled={noKeyframes}
          aria-label="Restart"
        >
          ↺
        </button>
        <input
          type="range"
          className="drill-player__scrub"
          min={0}
          max={total || 1}
          step={0.05}
          value={time}
          onChange={(e) => handleScrub(parseFloat(e.target.value))}
          disabled={noKeyframes}
          aria-label="Playback position"
        />
        <span className="drill-player__time" aria-live="off">
          {time.toFixed(1)}s / {total.toFixed(1)}s
        </span>
        <div className="drill-player__speeds" role="group" aria-label="Playback speed">
          {[0.5, 1, 2].map((s) => (
            <button
              key={s}
              type="button"
              className="drill-player__btn drill-player__btn--speed"
              data-active={speed === s || undefined}
              onClick={() => setSpeed(s)}
              disabled={noKeyframes}
            >
              {s}×
            </button>
          ))}
        </div>
      </div>
      {!noKeyframes && (
        <div className="drill-player__keyframes" role="group" aria-label="Jump to keyframe">
          {keyframes.map((kf, i) => (
            <button
              key={kf.id}
              type="button"
              className="drill-player__keyframe-chip"
              onClick={() => {
                const start = i === 0 ? 0 : keyframeBoundaries[i - 1];
                handleScrub(start);
                setPlaying(false);
              }}
              aria-label={`Jump to keyframe ${i + 1}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
      {noKeyframes && (
        <p className="drill-player__static-hint">
          Static drill — no animation keyframes yet.
        </p>
      )}
    </div>
  );
}

export { extractDrillSpec };
